<template>
  <page-section :title="$t('ProfilesEvents_Title')">
    <rest-list :payload="fetchEndpoint" :loading="!applicationsLoaded">
      <rest-list-column-date
        :prop="clickhouse ? 'timestamp' : 'create_time'"
        :unix="!clickhouse"
        :title="$t('ProfilesEvents_Field_Timestamp')"
        :width="200"
      />

      <rest-list-column-enum
        v-if="clickhouseStandardized && systemEvents"
        prop="event_type"
        :title="$t('ProfilesEvents_Field_Type')"
        :sortable="false"
        :width="200"
        :enum="EventType"
      />

      <rest-list-column
        :prop="clickhouse ? 'event_name' : 'event_title'"
        :title="$t('ProfilesEvents_Field_Title')"
        :sortable="false"
        :width="300"
      />

      <el-table-column
        :sortable="false"
        :min-width="200"
        v-if="clickhouseStandardized"
      >
        <template slot="header" slot-scope="{}">
          <span>
            {{ $t('ProfilesEvents_Field_Data') }}
          </span>
          <rest-list-filter-input prop="data" />
        </template>
        <template slot-scope="scope">
          <el-button
            type="primary"
            plain
            size="mini"
            @click="showEventDataPopup(scope.row)"
          >
            {{ $t('ProfilesEvents_JSON_Button_title') }}
            <el-icon name="view" />
          </el-button>
        </template>
      </el-table-column>

      <el-table-column
        :sortable="false"
        :min-width="200"
        v-if="clickhouseLegacy"
      >
        <template slot="header" slot-scope="{}">
          <span>
            {{ $t('ProfilesEvents_Field_Properties') }}
          </span>
          <rest-list-filter-input :prop="propertiesField" />
        </template>
        <template slot-scope="scope">
          <el-button
            type="primary"
            plain
            size="mini"
            @click="showEventDataPopup(scope.row, propertiesField)"
          >
            {{ $t('ProfilesEvents_JSON_Button_title') }}
            <el-icon name="view" />
          </el-button>
        </template>
      </el-table-column>

      <el-table-column :sortable="false" :min-width="200" v-if="!clickhouse">
        <template slot="header" slot-scope="{}">
          <span>
            {{ $t('ProfilesEvents_Field_Properties') }}
          </span>
          <rest-list-filter-input :prop="propertiesField" />
        </template>
        <template slot-scope="scope">
          <span v-if="isEmptyValue(scope.row[propertiesField])">-</span>
          <el-button
            type="primary"
            plain
            size="mini"
            v-else-if="isValidJson(scope.row[propertiesField])"
            @click="showDataPopup(scope.row[propertiesField])"
          >
            {{ $t('ProfilesEvents_JSON_Button_title') }}
            <el-icon name="view" />
          </el-button>
          <template v-else>{{ scope.row[propertiesField] }}</template>
        </template>
      </el-table-column>

      <el-table-column
        :sortable="false"
        :min-width="200"
        v-if="clickhouseLegacy"
      >
        <template slot="header" slot-scope="{}">
          <span>
            {{ $t('ProfilesEvents_Field_UserAttributes') }}
          </span>
          <rest-list-filter-input prop="user_attributes" />
        </template>
        <template slot-scope="scope">
          <el-button
            type="primary"
            plain
            size="mini"
            @click="showEventDataPopup(scope.row, 'user_attributes')"
          >
            {{ $t('ProfilesEvents_JSON_Button_title') }}
            <el-icon name="view" />
          </el-button>
        </template>
      </el-table-column>

      <rest-list-column
        prop="device_id"
        :title="$t('ProfilesEvents_Field_Device')"
        :route="rowClickDeviceIdRoute"
        :width="200"
        :sortable="false"
      />

      <rest-list-column-enum
        v-if="clickhouse"
        :prop="sourceField"
        :title="$t('ProfilesEvents_Field_Source')"
        :enum="AuditLogSource"
        :width="200"
        :sortable="false"
      />

      <rest-list-column-enum
        :prop="applicationField"
        :key="applicationsLoaded"
        :title="$t('ProfilesEvents_Field_Application')"
        :enum="applications"
        :width="200"
        :sortable="false"
      />

      <rest-list-column-enum
        :prop="integrationField"
        :key="integrationsLoaded"
        :title="$t('ProfilesEvents_Field_Integration')"
        :enum="integrations"
        :width="200"
        :sortable="false"
      />

      <rest-list-column
        v-if="clickhouseStandardized && systemEvents"
        prop="context.campaign.name"
        :title="$t('ProfilesEvents_Field_Campaign')"
        :route="rowClickCampaignRoute"
        :width="200"
        :sortable="false"
      />

      <rest-list-column-date
        v-if="clickhouse"
        prop="received_at"
        :unix="false"
        :title="$t('ProfilesEvents_Field_ReceivedAt')"
        :width="200"
      />

      <rest-list-column-date
        v-if="clickhouse"
        prop="loaded_at"
        :unix="false"
        :title="$t('ProfilesEvents_Field_LoadedAt')"
        :width="200"
      />
    </rest-list>

    <el-dialog
      :title="dataPopup"
      :visible="!!dataPopup"
      :modal="false"
      :modal-append-to-body="false"
      :before-close="closeDataPopup"
    >
      <div v-loading="dataPopupLoading" class="json-wrapper">
        <vue-json-pretty :data="jsonData" />
      </div>
    </el-dialog>
  </page-section>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import VueJsonPretty from 'vue-json-pretty'
import RestListFilterInput from '@/common/components/rest/RestListFilterInput'
import PageSection from '@/common/components/page/PageSection'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import enquoteBigNumber from '@/common/helpers/JSONBigIntTransfromer'
import { AuditLogSource } from '@/modules/profiles/enum/AuditLog'
import { EventType } from '@/common/enum/EventType'

export default {
  name: 'EventsListPage',
  components: {
    PageSection,
    RestListFilterInput,
    RestListColumnDate,
    RestList,
    RestListColumnEnum,
    RestListColumn,
    VueJsonPretty
  },
  data() {
    return {
      dataPopup: false,
      dataPopupLoading: false,
      jsonData: null,
      applications: {},
      applicationsLoaded: false,
      integrations: {},
      integrationsLoaded: false,
      AuditLogSource,
      EventType
    }
  },
  created() {
    new RestApiPaginator(this.applicationsEndpoint)
      .fetchAllPages()
      .then(data => {
        data.forEach(val => {
          this.applications[val.id] = val.name
        })
        this.applicationsLoaded = true
      })

    new RestApiPaginator(this.integrationsEndpoint)
      .fetchAllPages()
      .then(data => {
        data.forEach(val => {
          this.integrations[val.id] = val.name
        })
        this.integrationsLoaded = true
      })
  },

  methods: {
    showDataPopup(json, title) {
      if (typeof json === 'object') {
        this.jsonData = json
      } else {
        this.jsonData = JSON.parse(enquoteBigNumber(json))
      }
      this.dataPopup = title || this.$t('ProfilesEvents_JSON_Data_Title')
    },
    showEventDataPopup(row, field = null) {
      this.jsonData = null
      this.dataPopupLoading = true
      this.dataPopup = field
        ? this.$t('ProfilesEvents_JSON_Data_Title')
        : row.event_name || this.$t('ProfilesEvents_JSON_Data_Title')

      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'event-hits-clickhouse/data',
          params: {
            profile_id: this.$route.query.profile_id,
            event_name: row.event_name,
            uuid: row.uuid,
            timestamp: row.timestamp,
            event_type: row.event_type
          }
        })
      )
        .then(response => {
          const data = field ? response.data[field] : response.data
          if (typeof data === 'object') {
            this.jsonData = data
          } else {
            this.jsonData = JSON.parse(enquoteBigNumber(data))
          }
        })
        .catch(error => {
          this.$message({ message: error.message, type: 'error' })
          this.closeDataPopup()
        })
        .finally(() => {
          this.dataPopupLoading = false
        })
    },
    closeDataPopup() {
      this.dataPopup = false
      this.dataPopupLoading = false
    },
    isValidJson(value) {
      if (typeof value === 'object') {
        return true
      } else if (typeof value === 'string') {
        try {
          if (JSON.parse(value)) {
            return true
          }
        } catch (e) {
          return false
        }
      }
      return false
    },
    isEmptyValue(value) {
      if (value === null) {
        // typeof null returns 'object' early return if it is null
        return true
      }
      if (Array.isArray(value)) {
        return value.length === 0
      } else if (typeof value === 'object') {
        return Object.keys(value).length === 0
      } else if (typeof value === 'string') {
        return value === '[]' || value === '{}' || value === ''
      }
    },
    rowClickDeviceIdRoute(row) {
      return projectRoute({
        name: 'Device',
        query: {
          device_id: row.device_id
        }
      })
    },
    rowClickCampaignRoute(row) {
      if (row.context.campaign) {
        return projectRoute({
          name: 'ViewCampaign',
          query: {
            id: row.context.campaign.id
          }
        })
      }
      return null
    }
  },

  computed: {
    systemEvents() {
      return this.$hasFeature('displaySystemEvents')
    },
    clickhouse() {
      return this.$hasFeature('shouldReadEventsFromClickhouse')
    },
    clickhouseLegacy() {
      return (
        this.$hasFeature('shouldReadEventsFromClickhouse') &&
        !this.$hasFeature('standardizedClickhouseEvents')
      )
    },
    clickhouseStandardized() {
      return (
        this.$hasFeature('shouldReadEventsFromClickhouse') &&
        this.$hasFeature('standardizedClickhouseEvents')
      )
    },
    propertiesField() {
      return this.clickhouse ? 'properties' : 'value'
    },
    sourceField() {
      return this.clickhouseStandardized ? 'source' : 'source_channel'
    },
    applicationField() {
      if (this.clickhouse) {
        return this.clickhouseStandardized
          ? 'context.application.id'
          : 'source_application_id'
      } else {
        return 'application_id'
      }
    },
    integrationField() {
      if (this.clickhouse) {
        return this.clickhouseStandardized
          ? 'context.integration.id'
          : 'source_integration_id'
      } else {
        return 'integration_id'
      }
    },
    applicationsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'applications',
        params: {}
      })
    },
    integrationsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'integrations',
        params: {}
      })
    },
    fetchEndpoint() {
      if (this.clickhouse) {
        return projectEndpoint({
          method: 'get',
          url: `event-hits-clickhouse`,
          params: {
            profile_id: this.$route.query.profile_id,
            sort: '-timestamp'
          }
        })
      } else {
        return projectEndpoint({
          method: 'get',
          url: `event-hits`,
          params: {
            expand: 'event_title',
            profile_id: this.$route.query.profile_id,
            sort: '-create_time'
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.json-wrapper {
  overflow-y: auto;
  max-height: 400px;
}
</style>



// WEBPACK FOOTER //
// src/modules/profiles/pages/EventsListPage.vue